import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AxiosError } from 'axios';

import MainDialogLayout from '@/components/ui/MainDialogLayout/MainDialogLayout';
import Button from '@/components/ui/Button';
import { usePostAuthApi } from '@/hooks/UseApiCalls';
import { DocumentDtoOut } from '@/models/out/DocumentDtoOut';
import { EmptyFileError, FileReadError, FileReaderHelper, translateFileReadError } from '@/helpers/fileReaderHelper';

/** Mirrors FundReactivationRequestsService: the server refuses more, this just says so before the upload. */
const MAX_FILES = 5;
const MAX_FILE_SIZE_MB = 8;
const MAX_TOTAL_SIZE_MB = 18;
const MB = 1024 * 1024;

interface SendReactivationDocumentsDialogProps {
  open: boolean;
  onClose: () => void;
  fundId: number;
  onSent: () => void;
}

const extractServerError = (error: unknown): string | null => {
  const data = (error as AxiosError).response?.data;
  if (!data) return null;
  if (typeof data === 'string') return data;
  if (typeof data === 'object' && 'message' in data) return String((data as { message: unknown }).message);
  return null;
};

/**
 * HAT-1301: the coordinator of a frozen project sends their own identity document to the office.
 * Nothing is reactivated here - the office opens the files and decides - so the dialog only has to
 * make sure the right kind of file goes out within the limits the server enforces.
 */
const SendReactivationDocumentsDialog = ({ open, onClose, fundId, onSent }: SendReactivationDocumentsDialogProps) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);

  const { mutateAsync: send } = usePostAuthApi<void, { documents: DocumentDtoOut[] }>(
    `funds/financial-data/${fundId}/reactivation-request`
  );

  useEffect(() => {
    if (open) {
      setFiles([]);
      setError(null);
      setIsSending(false);
    }
  }, [open]);

  const addFiles = (picked: FileList | null) => {
    if (!picked) return;
    setError(null);
    let next = [...files];
    for (const file of Array.from(picked)) {
      if (file.size === 0) {
        setError(t('emptyFileSelected', { name: file.name }));
        continue;
      }
      if (file.size > MAX_FILE_SIZE_MB * MB) {
        setError(t('sendReactivationDialog.fileTooLarge', { name: file.name, max: MAX_FILE_SIZE_MB }));
        continue;
      }
      if (next.length >= MAX_FILES) {
        setError(t('sendReactivationDialog.tooManyFiles', { max: MAX_FILES }));
        break;
      }
      if (next.reduce((sum, f) => sum + f.size, 0) + file.size > MAX_TOTAL_SIZE_MB * MB) {
        setError(t('sendReactivationDialog.totalTooLarge', { max: MAX_TOTAL_SIZE_MB }));
        continue;
      }
      next = [...next, file];
    }
    setFiles(next);
  };

  const submit = async () => {
    if (files.length === 0) {
      setError(t('sendReactivationDialog.documentsRequired'));
      return;
    }
    setError(null);
    setIsSending(true);
    try {
      const documents: DocumentDtoOut[] = [];
      for (const file of files) {
        const [document] = await FileReaderHelper.fileToDocumentDtoOut(file);
        documents.push(document);
      }
      await send({ documents });
      onSent();
      onClose();
    } catch (err) {
      if (err instanceof EmptyFileError || err instanceof FileReadError) {
        setError(translateFileReadError(err, t));
      } else {
        setError(extractServerError(err) ?? t('sendReactivationDialog.sendError'));
      }
    } finally {
      setIsSending(false);
    }
  };

  return (
    <MainDialogLayout
      open={open}
      onOpenChange={(isOpen: boolean) => {
        if (!isOpen && !isSending) onClose();
      }}
      title={t('sendReactivationDialog.title')}
      maxWidth="600px"
      fitContent
    >
      <div className="flex flex-col gap-4 w-full px-[26px] pb-[22px]">
        <p className="body-md-regular m-0">{t('sendReactivationDialog.intro')}</p>
        <p className="body-sm-regular m-0 text-(--font-body-2)">
          {t('sendReactivationDialog.limits', { files: MAX_FILES, size: MAX_FILE_SIZE_MB })}
        </p>

        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={() => fileInputRef.current?.click()} disabled={isSending}>
            {t('sendReactivationDialog.chooseFiles')}
          </Button>
          {files.length === 0 && (
            <span className="body-sm-regular text-(--font-body-3)">{t('sendReactivationDialog.noFiles')}</span>
          )}
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="application/pdf,image/jpeg,image/png,image/webp"
          multiple
          className="hidden"
          onChange={(e) => {
            addFiles(e.target.files);
            e.target.value = '';
          }}
        />
        {files.length > 0 && (
          <ul className="flex flex-col gap-1 m-0 p-0 list-none">
            {files.map((file, index) => (
              <li key={`${file.name}-${index}`} className="flex items-center gap-2 body-sm-regular">
                <span className="truncate">{file.name}</span>
                <button
                  type="button"
                  className="body-sm-regular text-red-600 underline bg-transparent border-0 cursor-pointer"
                  onClick={() => setFiles((current) => current.filter((_, i) => i !== index))}
                  disabled={isSending}
                >
                  {t('sendReactivationDialog.removeFile')}
                </button>
              </li>
            ))}
          </ul>
        )}

        {error && (
          <p role="alert" className="body-sm-regular m-0 text-red-600">
            {error}
          </p>
        )}

        <div className="flex gap-2.5 justify-end mt-2">
          <Button variant="white" onClick={onClose} disabled={isSending}>
            {t('fundsManagement.cancel')}
          </Button>
          <Button variant="primary" onClick={submit} disabled={isSending}>
            {isSending ? t('loading') : t('sendReactivationDialog.send')}
          </Button>
        </div>
      </div>
    </MainDialogLayout>
  );
};

export default SendReactivationDocumentsDialog;
